/**
 * Professional Frontend Logger Utility
 * 
 * Enterprise-grade logging system for the YIM frontend application.
 * Features:
 * - Captures browser context (User-Agent, OS, URL, screen dimensions)
 * - Remote error reporting to backend audit endpoint
 * - Request ID correlation for full-stack traceability
 * - Console logging with levels (debug, info, warn, error)
 * - Performance tracking and metrics
 */

import { getRequestState } from '../store/useRequestStore';
import { getAuthState } from '../store/useAuthStore';

// ============================================
// TYPES
// ============================================

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface BrowserContext {
  userAgent: string;
  language: string;
  platform: string;
  screenWidth: number;
  screenHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  pixelRatio: number;
  online: boolean;
  cookiesEnabled: boolean;
  doNotTrack: boolean;
  timezone: string;
  url: string;
  referrer: string;
}

export interface ClientErrorReport {
  // Error details
  type: 'ERROR' | 'UNHANDLED_REJECTION' | 'NETWORK_ERROR' | 'COMPONENT_ERROR' | 'MANUAL';
  message: string;
  stack?: string;
  componentStack?: string;

  // Context
  requestId: string | null;
  userId?: string;
  userEmail?: string;
  url: string;
  timestamp: string;

  // Browser info
  browser: BrowserContext;

  // Additional metadata
  metadata?: Record<string, unknown>;
}

export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: string;
  requestId?: string | null;
  data?: unknown;
}

// ============================================
// BROWSER CONTEXT UTILITIES
// ============================================

/**
 * Capture comprehensive browser context
 */
function getBrowserContext(): BrowserContext {
  const ua = navigator.userAgent;

  return {
    userAgent: ua,
    language: navigator.language,
    platform: navigator.platform || 'unknown',
    screenWidth: screen.width,
    screenHeight: screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    pixelRatio: window.devicePixelRatio || 1,
    online: navigator.onLine,
    cookiesEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack === '1',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    url: window.location.href,
    referrer: document.referrer || 'direct',
  };
}

/**
 * Parse browser name and version from user agent
 */
function parseBrowserInfo(userAgent: string): { browser: string; version: string; os: string } {
  let browser = 'Unknown';
  let version = 'Unknown';
  let os = 'Unknown';

  // Detect browser
  if (userAgent.includes('Firefox/')) {
    browser = 'Firefox';
    version = userAgent.match(/Firefox\/([\d.]+)/)?.[1] || 'Unknown';
  } else if (userAgent.includes('Edg/')) {
    browser = 'Edge';
    version = userAgent.match(/Edg\/([\d.]+)/)?.[1] || 'Unknown';
  } else if (userAgent.includes('Chrome/')) {
    browser = 'Chrome';
    version = userAgent.match(/Chrome\/([\d.]+)/)?.[1] || 'Unknown';
  } else if (userAgent.includes('Safari/') && !userAgent.includes('Chrome')) {
    browser = 'Safari';
    version = userAgent.match(/Version\/([\d.]+)/)?.[1] || 'Unknown';
  }

  // Detect OS
  if (userAgent.includes('Windows')) {
    os = 'Windows';
  } else if (userAgent.includes('Mac OS')) {
    os = 'macOS';
  } else if (userAgent.includes('Linux')) {
    os = 'Linux';
  } else if (userAgent.includes('Android')) {
    os = 'Android';
  } else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) {
    os = 'iOS';
  }

  return { browser, version, os };
}

// ============================================
// REMOTE REPORTING
// ============================================

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
const CLIENT_ERRORS_ENDPOINT = `${BASE_URL}/audit-logs/client-errors`;

/**
 * Send client error report to backend
 */
async function sendErrorReport(report: ClientErrorReport): Promise<boolean> {
  try {
    const response = await fetch(CLIENT_ERRORS_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Include auth token if available
        ...(getAuthState().accessToken && {
          'Authorization': `Bearer ${getAuthState().accessToken}`,
        }),
      },
      body: JSON.stringify(report),
    });

    return response.ok;
  } catch {
    // Don't throw - logging should not break the app
    return false;
  }
}

// ============================================
// ERROR QUEUE (for offline support)
// ============================================

const ERROR_QUEUE_KEY = 'YIM_ERROR_QUEUE';
const MAX_QUEUED_ERRORS = 50;

interface QueuedError {
  report: ClientErrorReport;
  attempts: number;
  lastAttempt: number;
}

function getErrorQueue(): QueuedError[] {
  try {
    const stored = localStorage.getItem(ERROR_QUEUE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveErrorQueue(queue: QueuedError[]): void {
  try {
    // Keep only the most recent errors
    const trimmed = queue.slice(-MAX_QUEUED_ERRORS);
    localStorage.setItem(ERROR_QUEUE_KEY, JSON.stringify(trimmed));
  } catch {
    // Storage full or unavailable
  }
}

function queueError(report: ClientErrorReport): void {
  const queue = getErrorQueue();
  queue.push({
    report,
    attempts: 0,
    lastAttempt: 0,
  });
  saveErrorQueue(queue);
}

async function flushErrorQueue(): Promise<void> {
  if (!navigator.onLine) return;

  const queue = getErrorQueue();
  if (queue.length === 0) return;

  const remaining: QueuedError[] = [];

  for (const item of queue) {
    if (item.attempts >= 3) continue; // Skip after 3 attempts

    const success = await sendErrorReport(item.report);
    if (!success) {
      item.attempts++;
      item.lastAttempt = Date.now();
      remaining.push(item);
    }
  }

  saveErrorQueue(remaining);
}

// Flush queue when coming back online
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    setTimeout(flushErrorQueue, 1000);
  });
}

// ============================================
// LOGGER CLASS
// ============================================

class Logger {
  private context: string;
  private isDev: boolean;

  constructor(context: string = 'App') {
    this.context = context;
    this.isDev = import.meta.env.DEV;
  }

  /**
   * Create a new logger instance with context
   */
  createLogger(context: string): Logger {
    return new Logger(context);
  }

  /**
   * Format log message with timestamp and context
   */
  private formatMessage(level: LogLevel, message: string): string {
    const timestamp = new Date().toISOString();
    const requestId = getRequestState().lastRequestId;
    const reqIdStr = requestId ? `[${requestId.slice(0, 8)}]` : '';
    return `${timestamp} [${level.toUpperCase()}] [${this.context}]${reqIdStr} ${message}`;
  }

  /**
   * Log to console (dev only for debug/info)
   */
  private logToConsole(level: LogLevel, message: string, data?: unknown): void {
    const formatted = this.formatMessage(level, message);

    switch (level) {
      case 'debug':
        // eslint-disable-next-line no-console -- logger utility, gated behind isDev
        if (this.isDev) console.debug(formatted, data || '');
        break;
      case 'info':
        // eslint-disable-next-line no-console -- logger utility, gated behind isDev
        if (this.isDev) console.info(formatted, data || '');
        break;
      case 'warn':
        console.warn(formatted, data || '');
        break;
      case 'error':
        console.error(formatted, data || '');
        break;
    }
  }

  /**
   * Debug level logging (development only)
   */
  debug(message: string, data?: unknown): void {
    this.logToConsole('debug', message, data);
  }

  /**
   * Info level logging (development only)
   */
  info(message: string, data?: unknown): void {
    this.logToConsole('info', message, data);
  }

  /**
   * Warning level logging
   */
  warn(message: string, data?: unknown): void {
    this.logToConsole('warn', message, data);
  }

  /**
   * Error level logging
   */
  error(message: string, data?: unknown): void {
    this.logToConsole('error', message, data);
  }

  /**
   * Report an error to the backend for admin visibility
   */
  async reportError(
    error: Error,
    type: ClientErrorReport['type'] = 'ERROR',
    metadata?: Record<string, unknown>,
    componentStack?: string
  ): Promise<void> {
    const authState = getAuthState();
    const requestState = getRequestState();

    const report: ClientErrorReport = {
      type,
      message: error.message,
      stack: error.stack,
      componentStack,
      requestId: requestState.lastRequestId,
      userId: authState.user?.id,
      userEmail: authState.user?.email,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      browser: getBrowserContext(),
      metadata,
    };

    // Log locally
    this.error(`${type}: ${error.message}`, { stack: error.stack, metadata });

    // Send to backend (or queue if offline)
    if (navigator.onLine) {
      await sendErrorReport(report);
    } else {
      queueError(report);
    }
  }

  /**
   * Report a component error (from ErrorBoundary)
   */
  async reportComponentError(
    error: Error,
    componentStack: string
  ): Promise<void> {
    await this.reportError(error, 'COMPONENT_ERROR', undefined, componentStack);
  }

  /**
   * Track performance metrics
   */
  trackPerformance(name: string, duration: number): void {
    if (this.isDev) {
      this.debug(`Performance [${name}]: ${duration.toFixed(2)}ms`);
    }
  }

  /**
   * Time a function execution
   */
  async time<T>(name: string, fn: () => Promise<T>): Promise<T> {
    const start = performance.now();
    try {
      return await fn();
    } finally {
      const duration = performance.now() - start;
      this.trackPerformance(name, duration);
    }
  }
}

// ============================================
// GLOBAL ERROR HANDLERS
// ============================================

const globalLogger = new Logger('Global');

// Catch unhandled errors
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    globalLogger.reportError(
      event.error || new Error(event.message),
      'ERROR',
      {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      }
    );
  });

  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason));

    globalLogger.reportError(error, 'UNHANDLED_REJECTION');
  });
}

// ============================================
// EXPORTS
// ============================================

export const logger = new Logger('App');
export const createLogger = (context: string) => new Logger(context);

// Export utilities
export { getBrowserContext, parseBrowserInfo, flushErrorQueue };

export default logger;
